"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, PhoneCall, ShieldAlert, Volume2, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SymptomItem {
  id: string;
  title: string;
  badge: string;
  severity: "urgent" | "warning" | "standard";
  description: string;
  actionText: string;
  linkUrl: string;
  iconName: string;
}
const symptomsList: SymptomItem[] = [{
  id: "running-toilet",
  title: "Constantly Running Toilet",
  badge: "High Water Bill",
  severity: "warning",
  description: "Water keeps trickling into the bowl, wasting dozens of gallons daily due to worn flappers, fill valves, or broken floats.",
  actionText: "Fix Running Toilet",
  linkUrl: "/toilet-repair/",
  iconName: "Droplets"
}, {
  id: "overflowing-bowl",
  title: "Toilet Overflowing or Backing Up",
  badge: "Emergency Priority",
  severity: "urgent",
  description: "Water rises rapidly or spills over onto bathroom floors. Requires immediate emergency water shut-off and rapid drain clearing.",
  actionText: "Get Emergency Help",
  linkUrl: "/emergency-plumbing/",
  iconName: "ShieldAlert"
}, {
  id: "weak-flush",
  title: "Weak or Incomplete Flush",
  badge: "Partial Clog",
  severity: "standard",
  description: "The flush lacks power or requires multiple attempts to clear, signaling mineral buildup, low tank levels, or partial blockage.",
  actionText: "View Flush Repair Options",
  linkUrl: "/toilet-repair/",
  iconName: "Waves"
}, {
  id: "leaking-base",
  title: "Water Leaking Around the Base",
  badge: "Subfloor Damage Risk",
  severity: "urgent",
  description: "Puddles forming at the base of the porcelain indicate a broken wax ring seal or cracked flange needing immediate replacement.",
  actionText: "Inspect Base Leaks",
  linkUrl: "/leak-detection-repair/",
  iconName: "Flame"
}, {
  id: "gurgling-sounds",
  title: "Gurgling Bubbles or Slow Drain",
  badge: "Main Line Warning",
  severity: "warning",
  description: "Bubbling sounds in the toilet when using nearby sinks or showers point to trapped air or venting and sewer pipe obstructions.",
  actionText: "Clear Main Drain",
  linkUrl: "/drain-cleaning/",
  iconName: "Volume2"
}, {
  id: "hissing-tank",
  title: "Slow Tank Refill & Hissing Noise",
  badge: "Component Wear",
  severity: "standard",
  description: "Loud hissing or whistling inside the tank with delayed refills indicates a clogged supply line, broken diaphragm, or faulty shut-off valve.",
  actionText: "Schedule Toilet Service",
  linkUrl: "/toilet-repair/",
  iconName: "Wrench"
}];
function getSymptomIcon(name: string) {
  switch (name) {
    case "ShieldAlert":
      return <AlertTriangle className="size-5 text-destructive" />;
    case "Droplets":
      return <Droplets className="size-5 text-primary" />;
    case "Flame":
      return <AlertTriangle className="size-5 text-accent-foreground" />;
    case "Volume2":
      return <Volume2 className="size-5 text-primary" />;
    case "Waves":
      return <Waves className="size-5 text-primary" />;
    case "Wrench":
    default:
      return <Wrench className="size-5 text-primary" />;
  }
}
export default function ToiletSymptoms() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="toiletsymptomsgrid" data-nav="light" className="bg-background py-16 md:py-24 text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            Diagnostic Symptom Checker
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Common Toilet Symptoms &amp; Warning Signs
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Identify what is wrong with your toilet to get the right fix fast. Choose your symptom below for targeted repair solutions in Jonesboro.
          </p>
        </div>

        {/* Symptoms Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {symptomsList.map((item, i) => <div key={item.id} data-index={i} className="h-full">
              <motion.div variants={itemVariants} className="h-full">
                <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 rounded-xl">
                  <CardHeader className="gap-3 pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="p-2.5 rounded-lg bg-secondary/80 flex items-center justify-center">
                        {getSymptomIcon(item.iconName)}
                      </div>
                      <Badge variant={item.severity === "urgent" ? "destructive" : item.severity === "warning" ? "secondary" : "outline"} className="text-xs">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground pt-1">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="flex-1 pb-4">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </CardDescription>
                  </CardContent>

                  <CardFooter className="pt-0">
                    <Button asChild variant={item.severity === "urgent" ? "default" : "outline"} className="w-full justify-between group">
                      <Link to={item.linkUrl}>
                        <span>{item.actionText}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        {/* Quick Help Banner */}
        <div className="mt-12 md:mt-16 bg-secondary/50 border border-border rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg sm:text-xl font-semibold text-foreground">
              Not sure which problem you are facing?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our 24/7 Jonesboro technicians can diagnose your toilet issue over the phone or on-site.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
              <Link to="/contact/">
                <PhoneCall className="size-4 mr-1" />
                <span>Contact Plumber Now</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}